/**
 * Pantalla del conglomerado asignado.
 *
 * Reúne lo que el encuestador necesita para ejecutar §5.3: el mapa del área,
 * las manzanas de arranque sorteadas, el salto sistemático k, la regla del
 * método de cumpleaños que le toca y el recorrido de viviendas ya contactadas.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ClusterMap, TilesWarning, type MapMarker } from '../components/LazyClusterMap';
import { CACHE_KEYS, db, getCache, type DraftHousehold } from '../db/db';
import { prefetchClusterGeometry } from '../engine/offline-geo';
import { useLivePosition } from '../hooks/useGps';
import { useApp } from '../store';
import type { GeoJsonFeatureCollection } from '../api/client';

export function AssignmentScreen() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { assignments, operator, isOnline, dispositions } = useApp();

  const assignment = assignments.find((a) => a.id === assignmentId);
  const [area, setArea] = useState<GeoJsonFeatureCollection | null>(null);
  const [households, setHouseholds] = useState<DraftHousehold[]>([]);
  const [showMap, setShowMap] = useState(true);
  const position = useLivePosition(showMap);
  const justSent = searchParams.get('enviada') === '1';

  /* --------------------------- Carga de datos --------------------------- */

  useEffect(() => {
    if (!assignment) return;
    let cancelled = false;

    void (async () => {
      const cached = await getCache<GeoJsonFeatureCollection>(
        CACHE_KEYS.geoCluster(assignment.cluster.id),
      );
      if (cancelled) return;
      if (cached) {
        setArea(cached.value);
        return;
      }
      if (!navigator.onLine) return;
      try {
        const fetched = await prefetchClusterGeometry(assignment.cluster.id);
        if (!cancelled) setArea(fetched);
      } catch {
        /* sin geometría: el mapa muestra el aviso */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [assignment]);

  useEffect(() => {
    if (!assignmentId) return;
    void db.households
      .where('assignmentId')
      .equals(assignmentId)
      .toArray()
      .then((rows) => setHouseholds(rows.sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))));
  }, [assignmentId, justSent]);

  /* ----------------------------- Marcadores ----------------------------- */

  const markers: MapMarker[] = useMemo(
    () =>
      households
        .filter((h) => h.latitude !== null && h.longitude !== null)
        .map((h) => ({
          latitude: h.latitude!,
          longitude: h.longitude!,
          kind:
            h.disposition === 'entrevista_completa'
              ? ('interview' as const)
              : h.disposition === 'rechazo'
                ? ('refusal' as const)
                : ('no-contact' as const),
          label: `#${h.sequenceNumber ?? '?'} · ${
            dispositions.find((d) => d.code === h.disposition)?.label ?? h.disposition
          }`,
        })),
    [households, dispositions],
  );

  if (!assignment) {
    return (
      <div className="center-screen">
        <div>
          <p className="muted">No se encontró la asignación.</p>
          <Link to="/" className="btn btn--secondary" style={{ marginTop: 12 }}>
            Volver
          </Link>
        </div>
      </div>
    );
  }

  const { cluster } = assignment;
  const completed = households.filter((h) => h.disposition === 'entrevista_completa').length;
  const nextSequence = Math.max(0, ...households.map((h) => h.sequenceNumber ?? 0)) + 1;

  return (
    <div className="app">
      <div className="topbar">
        <Link to="/" className="btn btn--ghost" style={{ minHeight: 'auto', padding: 4 }}>
          ‹ Inicio
        </Link>
        <div className="topbar__title">
          {cluster.id} · Sección {cluster.seccion}
          <span className="topbar__sub"> · estrato {cluster.stratum.id}</span>
        </div>
      </div>

      <main className="app__main">
        {justSent ? (
          <div className="notice notice--success">
            <span className="notice__title">Entrevista guardada</span>
            Se envió o quedó en la cola. Continúa con la siguiente vivienda del salto.
          </div>
        ) : null}

        {/* ------------------------- Plan operativo ------------------------- */}

        <div className="card">
          <p className="card__title">{cluster.colonia}</p>
          {!cluster.coloniaConfirmed ? (
            <p className="faint">
              Nombre de colonia de referencia preliminar (encarte 2016). Confírmalo en campo.
            </p>
          ) : null}
          <p className="card__meta" style={{ marginTop: 8 }}>
            {cluster.areaLabel} · meta {assignment.targetInterviews} entrevistas
          </p>

          <div className="divider" />

          <div className="stack">
            <div className="row row--between">
              <span className="muted">Salto sistemático</span>
              <span className="strong">
                {assignment.systematicSkipK !== null
                  ? `1 de cada ${assignment.systematicSkipK} viviendas`
                  : 'sin definir'}
              </span>
            </div>
            <div className="row row--between">
              <span className="muted">Selección en la vivienda</span>
              <span className="strong">
                {assignment.birthdayRule === 'ultimo'
                  ? 'Último cumpleaños'
                  : 'Próximo cumpleaños'}
              </span>
            </div>
            <div className="row row--between">
              <span className="muted">Avance</span>
              <span className="strong">
                {completed} de {assignment.targetInterviews}
              </span>
            </div>
          </div>

          {assignment.systematicSkipK === null ? (
            <div className="notice notice--warning" style={{ marginTop: 12, marginBottom: 0 }}>
              El coordinador aún no registró las viviendas estimadas del conglomerado, así que no
              hay salto calculado. Consúltalo antes de iniciar el recorrido.
            </div>
          ) : null}
        </div>

        {/* ----------------------- Regla de cumpleaños ---------------------- */}

        <div className="notice notice--info">
          <span className="notice__title">Cómo eliges a la persona</span>
          Lista a los adultos de la vivienda con credencial para votar y entrevista a quien{' '}
          <span className="strong">
            {assignment.birthdayRule === 'ultimo'
              ? 'cumplió años más recientemente'
              : 'cumpla años más próximamente'}
          </span>
          . Nunca la sustituyas por otra persona de la misma vivienda, aunque no esté: agenda
          revisita, hasta 3 intentos en horarios distintos.
        </div>

        {/* -------------------------- Manzanas ----------------------------- */}

        <h2 className="screen-title" style={{ fontSize: 18, marginTop: 20 }}>
          Manzanas de arranque
        </h2>
        {cluster.manzanas.length === 0 ? (
          <div className="card">
            <p className="card__meta">
              No hay manzanas sorteadas para este conglomerado. Avisa a tu coordinador: el
              sorteo se hace desde el panel. Mientras tanto, trabaja el área del polígono
              siguiendo el salto indicado.
            </p>
          </div>
        ) : (
          <ul className="list">
            {cluster.manzanas.map((manzana) => (
              <li className="card" key={manzana.id}>
                <div className="row row--between">
                  <p className="card__title" style={{ margin: 0 }}>
                    Manzana {manzana.orderIndex + 1}
                  </p>
                  {manzana.targetInterviews ? (
                    <span className="badge badge--neutral">
                      {manzana.targetInterviews} entrevistas
                    </span>
                  ) : null}
                </div>
                <p className="card__meta mono">{manzana.cvegeo ?? 'sin clave'}</p>
                {manzana.estimatedDwellings ? (
                  <p className="card__meta">{manzana.estimatedDwellings} viviendas habitadas</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {/* ----------------------------- Mapa ------------------------------ */}

        <div className="row row--between" style={{ marginTop: 20, marginBottom: 10 }}>
          <h2 className="screen-title" style={{ fontSize: 18, margin: 0 }}>
            Mapa del área
          </h2>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setShowMap((v) => !v)}
            style={{ minHeight: 'auto' }}
          >
            {showMap ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>

        {showMap ? (
          <>
            {area ? (
              <ClusterMap area={area} position={position} markers={markers} />
            ) : (
              <div className="map">
                <div className="map__placeholder">
                  {isOnline
                    ? 'Descargando el polígono del área…'
                    : 'El polígono no está en el dispositivo. Conéctate a WiFi para descargarlo.'}
                </div>
              </div>
            )}
            <TilesWarning />
            <p className="faint" style={{ marginTop: 8 }}>
              Verde: entrevista completa · Rojo: rechazo · Ámbar: sin contacto · Azul: tu posición
            </p>
          </>
        ) : null}

        {/* --------------------------- Recorrido --------------------------- */}

        <h2 className="screen-title" style={{ fontSize: 18, marginTop: 24 }}>
          Recorrido ({households.length} viviendas)
        </h2>

        {households.length === 0 ? (
          <p className="muted">Aún no has registrado viviendas en este conglomerado.</p>
        ) : (
          <ul className="list">
            {households.map((household) => {
              const disposition = dispositions.find((d) => d.code === household.disposition);
              return (
                <li className="card" key={household.clientUuid}>
                  <div className="row row--between">
                    <p className="card__title" style={{ margin: 0 }}>
                      #{household.sequenceNumber ?? '?'}
                    </p>
                    <span
                      className={`badge ${
                        household.disposition === 'entrevista_completa'
                          ? 'badge--ok'
                          : household.disposition === 'rechazo'
                            ? 'badge--err'
                            : 'badge--warn'
                      }`}
                    >
                      {disposition?.label ?? household.disposition}
                    </span>
                  </div>
                  {household.addressNote ? (
                    <p className="card__meta">{household.addressNote}</p>
                  ) : null}
                  <p className="faint">
                    {household.visits.length} intento(s) ·{' '}
                    {new Date(household.createdAt).toLocaleString('es-MX', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                  {/* Una revisita pendiente es trabajo por hacer, no un registro cerrado. */}
                  {disposition?.allowsRevisit && household.visits.length < 3 ? (
                    <button
                      type="button"
                      className="btn btn--secondary"
                      style={{ marginTop: 10 }}
                      onClick={() =>
                        navigate(
                          `/vivienda/${assignment.id}?revisita=${household.clientUuid}`,
                        )
                      }
                    >
                      Registrar intento {household.visits.length + 1} de 3
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </main>

      <div className="actionbar">
        <button
          type="button"
          className="btn btn--primary btn--block"
          onClick={() => navigate(`/vivienda/${assignment.id}?secuencia=${nextSequence}`)}
        >
          Nueva vivienda (#{nextSequence})
        </button>
      </div>
    </div>
  );
}
